import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { X, Plus, Trash2, CreditCard, MapPin, Star } from 'lucide-react';

function EditClientModal(props) {
  var formState = useState({
    name: props.client.name || '',
    email: props.client.email || '',
    phone: props.client.phone || '',
    company: props.client.company || '',
    status: props.client.status || 'new',
    tags: (props.client.tags || []).join(', '),
    budget: props.client.budget || '',
    lead_score: props.client.lead_score || ''
  });
  var form = formState[0];
  var setForm = formState[1];

  function handleChange(field, value) {
    var updated = {};
    for (var key in form) { updated[key] = form[key]; }
    updated[field] = value;
    setForm(updated);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={props.onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4" onClick={function(e) { e.stopPropagation(); }}>
        <h3 className="text-lg font-semibold mb-6">Edit Client</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input type="text" value={form.name} onChange={function(e) { handleChange('name', e.target.value); }}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={form.email} onChange={function(e) { handleChange('email', e.target.value); }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="tel" value={form.phone} onChange={function(e) { handleChange('phone', e.target.value); }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <input type="text" value={form.company} onChange={function(e) { handleChange('company', e.target.value); }}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={function(e) { handleChange('status', e.target.value); }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30">
                <option value="new">New</option>
                <option value="qualified">Qualified</option>
                <option value="negotiation">Negotiation</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lead Score</label>
              <input type="number" value={form.lead_score} onChange={function(e) { handleChange('lead_score', e.target.value); }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
            <input type="text" value={form.tags} onChange={function(e) { handleChange('tags', e.target.value); }}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" />
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button 
            onClick={function() { 
              var tags = form.tags ? form.tags.split(',').map(function(t) { return t.trim(); }) : [];
              props.onSave({
                name: form.name,
                email: form.email,
                phone: form.phone,
                company: form.company,
                status: form.status,
                tags: tags,
                lead_score: form.lead_score
              }); 
            }} 
            className="bg-[#014DB7]"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}

function AddProductModal(props) {
  var stateHooks = {
    selectedProduct: useState(null),
    billingType: useState(null),
    search: useState('')
  };
  var selectedProduct = stateHooks.selectedProduct[0];
  var setSelectedProduct = stateHooks.selectedProduct[1];
  var billingType = stateHooks.billingType[0];
  var setBillingType = stateHooks.billingType[1];
  var search = stateHooks.search[0];
  var setSearch = stateHooks.search[1];

  function getFilteredProducts() {
    if (!search) return props.products;
    var term = search.toLowerCase();
    var filtered = [];
    for (var i = 0; i < props.products.length; i++) {
      if (props.products[i].name.toLowerCase().indexOf(term) !== -1) {
        filtered.push(props.products[i]);
      }
    }
    return filtered;
  }

  function getAvailableBillingTypes(product) {
    var types = [];
    if (product.price_onetime) types.push({ key: 'onetime', label: 'One-Time', price: product.price_onetime });
    if (product.price_monthly) types.push({ key: 'monthly', label: 'Monthly', price: product.price_monthly });
    if (product.price_yearly) types.push({ key: 'yearly', label: 'Yearly', price: product.price_yearly });
    return types;
  }

  function renderProducts() {
    var filtered = getFilteredProducts();
    var elements = [];
    for (var i = 0; i < filtered.length; i++) {
      (function(product) {
        var isSelected = selectedProduct && selectedProduct.id === product.id;
        var availableTypes = getAvailableBillingTypes(product);
        
        elements.push(
          <div 
            key={product.id}
            onClick={function() { setSelectedProduct(product); setBillingType(null); }}
            className={"p-4 border rounded-lg cursor-pointer transition-all " + 
              (isSelected ? "border-[#014DB7] bg-blue-50" : "border-gray-200 hover:border-gray-300")}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Package className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{product.name}</p>
                  <div className="flex gap-2 mt-1">
                    {availableTypes.length > 0 ? availableTypes.map(function(t) {
                      var colors = { onetime: 'bg-purple-100 text-purple-700', monthly: 'bg-blue-100 text-blue-700', yearly: 'bg-green-100 text-green-700' };
                      return <span key={t.key} className={"px-2 py-0.5 rounded text-xs " + colors[t.key]}>${t.price}</span>;
                    }) : <span className="text-xs text-gray-400">No pricing</span>}
                  </div>
                </div>
              </div>
              {isSelected && <Check className="w-5 h-5 text-[#014DB7]" />}
            </div>
          </div>
        );
      })(filtered[i]);
    }
    return elements;
  }

  function renderBillingTypeButtons() {
    if (!selectedProduct) return null;
    var types = getAvailableBillingTypes(selectedProduct);
    if (types.length === 0) return <p className="text-sm text-gray-500">No pricing available for this product</p>;
    
    var colors = {
      onetime: { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700', selected: 'ring-purple-500' },
      monthly: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', selected: 'ring-blue-500' },
      yearly: { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700', selected: 'ring-green-500' }
    };
    
    return types.map(function(t) {
      var c = colors[t.key];
      var isSelected = billingType === t.key;
      return (
        <button
          key={t.key}
          onClick={function() { setBillingType(t.key); }}
          className={"flex-1 p-4 rounded-xl border-2 text-center transition-all " + 
            c.bg + " " + c.border + " " + (isSelected ? "ring-2 " + c.selected : "")}
        >
          <p className={"text-sm font-medium " + c.text}>{t.label}</p>
          <p className={"text-xl font-bold mt-1 " + c.text}>${t.price}</p>
        </button>
      );
    });
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={props.onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-xl w-full mx-4" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">Add Product</h3>
          <button onClick={props.onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <input
          type="text"
          placeholder="Search products..."
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          className="w-full px-4 py-2 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
        />

        <div className="space-y-2 max-h-48 overflow-y-auto mb-4">
          {props.products.length > 0 ? renderProducts() : (
            <div className="text-center py-8 text-gray-500">
              <p>No products available</p>
              <p className="text-sm mt-1">Create products in Billing first</p>
            </div>
          )}
        </div>

        {selectedProduct && (
          <div className="border-t pt-4 mb-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Select Billing Type</p>
            <div className="flex gap-3">
              {renderBillingTypeButtons()}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button 
            onClick={function() { if (selectedProduct && billingType) props.onAdd(selectedProduct, billingType); }}
            disabled={!selectedProduct || !billingType}
            className="bg-[#014DB7]"
          >
            Add Product
          </Button>
        </div>
      </div>
    </div>
  );
}

function AddServiceModal(props) {
  var stateHooks = {
    selectedService: useState(null),
    billingType: useState(null),
    search: useState('')
  };
  var selectedService = stateHooks.selectedService[0];
  var setSelectedService = stateHooks.selectedService[1];
  var billingType = stateHooks.billingType[0];
  var setBillingType = stateHooks.billingType[1];
  var search = stateHooks.search[0];
  var setSearch = stateHooks.search[1];

  function getFilteredServices() {
    if (!search) return props.services;
    var term = search.toLowerCase();
    var filtered = [];
    for (var i = 0; i < props.services.length; i++) {
      if (props.services[i].name.toLowerCase().indexOf(term) !== -1) {
        filtered.push(props.services[i]);
      }
    }
    return filtered;
  }

  function getAvailableBillingTypes(service) {
    var types = [];
    if (service.price_onetime) types.push({ key: 'onetime', label: 'One-Time', price: service.price_onetime });
    if (service.price_monthly) types.push({ key: 'monthly', label: 'Monthly', price: service.price_monthly });
    if (service.price_yearly) types.push({ key: 'yearly', label: 'Yearly', price: service.price_yearly });
    return types;
  }

  function renderServices() {
    var filtered = getFilteredServices();
    var elements = [];
    for (var i = 0; i < filtered.length; i++) {
      (function(service) {
        var isSelected = selectedService && selectedService.id === service.id;
        var availableTypes = getAvailableBillingTypes(service);
        
        elements.push(
          <div 
            key={service.id}
            onClick={function() { setSelectedService(service); setBillingType(null); }}
            className={"p-4 border rounded-lg cursor-pointer transition-all " + 
              (isSelected ? "border-[#014DB7] bg-purple-50" : "border-gray-200 hover:border-gray-300")}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Wrench className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{service.name}</p>
                  <div className="flex gap-2 mt-1">
                    {availableTypes.length > 0 ? availableTypes.map(function(t) {
                      var colors = { onetime: 'bg-purple-100 text-purple-700', monthly: 'bg-blue-100 text-blue-700', yearly: 'bg-green-100 text-green-700' };
                      return <span key={t.key} className={"px-2 py-0.5 rounded text-xs " + colors[t.key]}>${t.price}</span>;
                    }) : <span className="text-xs text-gray-400">No pricing</span>}
                  </div>
                </div>
              </div>
              {isSelected && <Check className="w-5 h-5 text-[#014DB7]" />}
            </div>
          </div>
        );
      })(filtered[i]);
    }
    return elements;
  }

  function renderBillingTypeButtons() {
    if (!selectedService) return null;
    var types = getAvailableBillingTypes(selectedService);
    if (types.length === 0) return <p className="text-sm text-gray-500">No pricing available for this service</p>;
    
    var colors = {
      onetime: { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700', selected: 'ring-purple-500' },
      monthly: { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700', selected: 'ring-blue-500' },
      yearly: { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700', selected: 'ring-green-500' }
    };
    
    return types.map(function(t) {
      var c = colors[t.key];
      var isSelected = billingType === t.key;
      return (
        <button
          key={t.key}
          onClick={function() { setBillingType(t.key); }}
          className={"flex-1 p-4 rounded-xl border-2 text-center transition-all " + 
            c.bg + " " + c.border + " " + (isSelected ? "ring-2 " + c.selected : "")}
        >
          <p className={"text-sm font-medium " + c.text}>{t.label}</p>
          <p className={"text-xl font-bold mt-1 " + c.text}>${t.price}</p>
        </button>
      );
    });
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={props.onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-xl w-full mx-4" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">Add Service</h3>
          <button onClick={props.onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <input
          type="text"
          placeholder="Search services..."
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          className="w-full px-4 py-2 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
        />

        <div className="space-y-2 max-h-48 overflow-y-auto mb-4">
          {props.services.length > 0 ? renderServices() : (
            <div className="text-center py-8 text-gray-500">
              <p>No services available</p>
              <p className="text-sm mt-1">Create services in Billing first</p>
            </div>
          )}
        </div>

        {selectedService && (
          <div className="border-t pt-4 mb-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Select Billing Type</p>
            <div className="flex gap-3">
              {renderBillingTypeButtons()}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button 
            onClick={function() { if (selectedService && billingType) props.onAdd(selectedService, billingType); }}
            disabled={!selectedService || !billingType}
            className="bg-[#014DB7]"
          >
            Add Service
          </Button>
        </div>
      </div>
    </div>
  );
}

function LocationModal(props) {
  var formState = useState({
    name: props.location ? props.location.name : '',
    address: props.location ? props.location.address : '',
    city: props.location ? props.location.city : '',
    state: props.location ? props.location.state : '',
    zip_code: props.location ? props.location.zip_code : '',
    phone: props.location ? props.location.phone : '',
    email: props.location ? props.location.email : ''
  });
  var form = formState[0];
  var setForm = formState[1];
  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  function handleChange(field, value) {
    var updated = {};
    for (var key in form) { updated[key] = form[key]; }
    updated[field] = value;
    setForm(updated);
  }

  function handleSave() {
    if (!form.name.trim()) {
      toast.error('Location name is required');
      return;
    }
    setSaving(true);
    props.onSave(form).finally(function() {
      setSaving(false);
    });
  }

  var US_STATES = [
    { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" },
    { code: "AZ", name: "Arizona" }, { code: "AR", name: "Arkansas" },
    { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" },
    { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" },
    { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
    { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" },
    { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
    { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" },
    { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
    { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" },
    { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
    { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" },
    { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
    { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" },
    { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
    { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" },
    { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
    { code: "DC", name: "District of Columbia" }
  ];

  function renderStateOptions() {
    var options = [<option key="" value="">Select state...</option>];
    for (var i = 0; i < US_STATES.length; i++) {
      var st = US_STATES[i];
      options.push(<option key={st.code} value={st.code}>{st.name}</option>);
    }
    return options;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={props.onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">{props.location ? 'Edit Location' : 'Add Location'}</h3>
          <button onClick={props.onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location Name *</label>
            <input 
              type="text" 
              value={form.name} 
              onChange={function(e) { handleChange('name', e.target.value); }}
              placeholder="e.g., Main Office, Warehouse, Branch #2"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
            <input 
              type="text" 
              value={form.address} 
              onChange={function(e) { handleChange('address', e.target.value); }}
              placeholder="123 Main Street"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input 
                type="text" 
                value={form.city} 
                onChange={function(e) { handleChange('city', e.target.value); }}
                placeholder="Sarasota"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <select 
                value={form.state} 
                onChange={function(e) { handleChange('state', e.target.value); }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              >
                {renderStateOptions()}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
              <input 
                type="text" 
                value={form.zip_code} 
                onChange={function(e) { handleChange('zip_code', e.target.value); }}
                placeholder="34236"
                maxLength={10}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input 
                type="tel" 
                value={form.phone} 
                onChange={function(e) { handleChange('phone', e.target.value); }}
                placeholder="(941) 555-1234"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input 
              type="email" 
              value={form.email} 
              onChange={function(e) { handleChange('email', e.target.value); }}
              placeholder="location@company.com"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300">
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Payment method can be added after saving the location
            </p>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button 
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
            className="bg-[#014DB7]"
          >
            {saving ? 'Saving...' : (props.location ? 'Save Changes' : 'Add Location')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AddCardModal(props) {
  var formState = useState({
    card_number: '',
    exp_month: '',
    exp_year: '',
    cvc: '',
    cardholder_name: '',
    location_id: ''
  });
  var form = formState[0];
  var setForm = formState[1];
  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  function handleChange(field, value) {
    var updated = {};
    for (var key in form) { updated[key] = form[key]; }
    updated[field] = value;
    setForm(updated);
  }

  function formatCardNumber(value) {
    var v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    var matches = v.match(/\d{4,16}/g);
    var match = matches && matches[0] || '';
    var parts = [];
    for (var i = 0; i < match.length; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length > 0 ? parts.join(' ') : value;
  }

  function detectCardBrand(number) {
    var n = number.replace(/\s/g, '');
    if (/^4/.test(n)) return 'visa';
    if (/^5[1-5]/.test(n)) return 'mastercard';
    if (/^3[47]/.test(n)) return 'amex';
    if (/^6(?:011|5)/.test(n)) return 'discover';
    return 'unknown';
  }

  function handleSave() {
    if (!form.card_number || !form.exp_month || !form.exp_year || !form.cvc) {
      toast.error('Please fill in all card details');
      return;
    }
    setSaving(true);
    var cardNum = form.card_number.replace(/\s/g, '');
    props.onSave({
      last_four: cardNum.slice(-4),
      brand: detectCardBrand(cardNum),
      exp_month: form.exp_month,
      exp_year: form.exp_year,
      cardholder_name: form.cardholder_name,
      location_id: form.location_id || null
    });
  }

  function renderLocationOptions() {
    var options = [<option key="" value="">No specific location</option>];
    for (var i = 0; i < props.locations.length; i++) {
      var loc = props.locations[i];
      options.push(<option key={loc.id} value={loc.id}>{loc.name}</option>);
    }
    return options;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={props.onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">Add Credit Card</h3>
          <button onClick={props.onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label>
            <input 
              type="text" 
              value={form.card_number}
              onChange={function(e) { handleChange('card_number', formatCardNumber(e.target.value)); }}
              placeholder="1234 5678 9012 3456"
              maxLength={19}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30 font-mono" 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cardholder Name</label>
            <input 
              type="text" 
              value={form.cardholder_name}
              onChange={function(e) { handleChange('cardholder_name', e.target.value.toUpperCase()); }}
              placeholder="JOHN DOE"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
            />
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select 
                value={form.exp_month}
                onChange={function(e) { handleChange('exp_month', e.target.value); }}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              >
                <option value="">MM</option>
                <option value="01">01</option>
                <option value="02">02</option>
                <option value="03">03</option>
                <option value="04">04</option>
                <option value="05">05</option>
                <option value="06">06</option>
                <option value="07">07</option>
                <option value="08">08</option>
                <option value="09">09</option>
                <option value="10">10</option>
                <option value="11">11</option>
                <option value="12">12</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <select 
                value={form.exp_year}
                onChange={function(e) { handleChange('exp_year', e.target.value); }}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              >
                <option value="">YY</option>
                <option value="25">25</option>
                <option value="26">26</option>
                <option value="27">27</option>
                <option value="28">28</option>
                <option value="29">29</option>
                <option value="30">30</option>
                <option value="31">31</option>
                <option value="32">32</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CVC</label>
              <input 
                type="text" 
                value={form.cvc}
                onChange={function(e) { handleChange('cvc', e.target.value.replace(/\D/g, '').slice(0, 4)); }}
                placeholder="123"
                maxLength={4}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30 font-mono" 
              />
            </div>
          </div>

          {props.locations.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link to Location (optional)</label>
              <select 
                value={form.location_id}
                onChange={function(e) { handleChange('location_id', e.target.value); }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              >
                {renderLocationOptions()}
              </select>
            </div>
          )}

          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
            <p className="text-xs text-blue-700">
              Card info is stored securely. When Stripe is configured, cards will be tokenized for PCI compliance.
            </p>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-[#014DB7]"
          >
            {saving ? 'Adding...' : 'Add Card'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function CreateInvoiceModal(props) {
  var itemsState = useState([{ name: '', quantity: 1, unit_price: 0, description: '' }]);
  var items = itemsState[0];
  var setItems = itemsState[1];
  var dueDateState = useState('');
  var dueDate = dueDateState[0];
  var setDueDate = dueDateState[1];
  var locationIdState = useState('');
  var locationId = locationIdState[0];
  var setLocationId = locationIdState[1];
  var notesState = useState('');
  var invoiceNotes = notesState[0];
  var setInvoiceNotes = notesState[1];
  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  // Combine products and services for dropdown
  var availableItems = [];
  for (var i = 0; i < (props.products || []).length; i++) {
    var p = props.products[i];
    availableItems.push({ id: p.id, name: p.name, price: p.price_monthly || p.price_one_time || 0, type: 'product' });
  }
  for (var j = 0; j < (props.services || []).length; j++) {
    var s = props.services[j];
    availableItems.push({ id: s.id, name: s.name, price: s.price_monthly || s.price_one_time || 0, type: 'service' });
  }

  function addItem() {
    var updated = [];
    for (var i = 0; i < items.length; i++) { updated.push(items[i]); }
    updated.push({ name: '', quantity: 1, unit_price: 0, description: '' });
    setItems(updated);
  }

  function removeItem(index) {
    var updated = [];
    for (var i = 0; i < items.length; i++) {
      if (i !== index) updated.push(items[i]);
    }
    setItems(updated);
  }

  function updateItem(index, field, value) {
    var updated = [];
    for (var i = 0; i < items.length; i++) {
      if (i === index) {
        var item = {};
        for (var k in items[i]) { item[k] = items[i][k]; }
        item[field] = value;
        updated.push(item);
      } else {
        updated.push(items[i]);
      }
    }
    setItems(updated);
  }

  function selectProduct(index, productId) {
    for (var i = 0; i < availableItems.length; i++) {
      if (availableItems[i].id === productId) {
        updateItem(index, 'name', availableItems[i].name);
        updateItem(index, 'unit_price', availableItems[i].price);
        break;
      }
    }
  }

  function calculateTotal() {
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      total += (items[i].quantity || 0) * (items[i].unit_price || 0);
    }
    return total;
  }

  function handleSave() {
    var validItems = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].name && items[i].quantity > 0) {
        validItems.push(items[i]);
      }
    }
    if (validItems.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    setSaving(true);
    props.onSave({
      client_id: props.client.id,
      client_name: props.client.name,
      client_email: props.client.email,
      location_id: locationId || null,
      due_date: dueDate || null,
      notes: invoiceNotes,
      items: validItems,
      status: 'pending'
    });
  }

  function renderLocationOptions() {
    var options = [<option key="" value="">No specific location</option>];
    for (var i = 0; i < (props.locations || []).length; i++) {
      var loc = props.locations[i];
      options.push(<option key={loc.id} value={loc.id}>{loc.name}</option>);
    }
    return options;
  }

  function renderProductOptions() {
    var options = [<option key="" value="">Select product/service...</option>];
    for (var i = 0; i < availableItems.length; i++) {
      var item = availableItems[i];
      options.push(
        <option key={item.id} value={item.id}>
          {item.name} (${item.price.toFixed(2)})
        </option>
      );
    }
    return options;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={props.onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">Create Invoice</h3>
          <button onClick={props.onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Client Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-500">Bill To</p>
          <p className="font-semibold text-gray-900">{props.client.name}</p>
          <p className="text-sm text-gray-600">{props.client.email}</p>
        </div>
        
        <div className="space-y-4">
          {/* Location & Due Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <select 
                value={locationId}
                onChange={function(e) { setLocationId(e.target.value); }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              >
                {renderLocationOptions()}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input 
                type="date" 
                value={dueDate}
                onChange={function(e) { setDueDate(e.target.value); }}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
              />
            </div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Line Items</label>
              <button 
                onClick={addItem}
                className="text-sm text-[#014DB7] hover:underline flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add Item
              </button>
            </div>
            
            <div className="space-y-2">
              {(function() {
                var rows = [];
                for (var i = 0; i < items.length; i++) {
                  (function(index, item) {
                    rows.push(
                      <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          {availableItems.length > 0 ? (
                            <select 
                              onChange={function(e) { selectProduct(index, e.target.value); }}
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
                            >
                              {renderProductOptions()}
                            </select>
                          ) : (
                            <input 
                              type="text" 
                              value={item.name}
                              onChange={function(e) { updateItem(index, 'name', e.target.value); }}
                              placeholder="Item name"
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
                            />
                          )}
                          <input 
                            type="text" 
                            value={item.name}
                            onChange={function(e) { updateItem(index, 'name', e.target.value); }}
                            placeholder="Or type custom item name"
                            className="w-full px-3 py-2 border rounded-lg text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
                          />
                        </div>
                        <div className="w-20">
                          <input 
                            type="number" 
                            min="1"
                            value={item.quantity}
                            onChange={function(e) { updateItem(index, 'quantity', parseInt(e.target.value) || 1); }}
                            placeholder="Qty"
                            className="w-full px-3 py-2 border rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
                          />
                          <p className="text-xs text-gray-400 text-center mt-1">Qty</p>
                        </div>
                        <div className="w-28">
                          <input 
                            type="number" 
                            step="0.01"
                            min="0"
                            value={item.unit_price}
                            onChange={function(e) { updateItem(index, 'unit_price', parseFloat(e.target.value) || 0); }}
                            placeholder="Price"
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30" 
                          />
                          <p className="text-xs text-gray-400 text-center mt-1">Unit Price</p>
                        </div>
                        <div className="w-24 text-right">
                          <p className="py-2 font-semibold text-gray-900">${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</p>
                          <p className="text-xs text-gray-400 mt-1">Subtotal</p>
                        </div>
                        {items.length > 1 && (
                          <button 
                            onClick={function() { removeItem(index); }}
                            className="p-2 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })(i, items[i]);
                }
                return rows;
              })()}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea 
              value={invoiceNotes}
              onChange={function(e) { setInvoiceNotes(e.target.value); }}
              placeholder="Additional notes for this invoice..."
              rows={2}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30 resize-none" 
            />
          </div>

          {/* Total */}
          <div className="bg-gray-900 text-white rounded-lg p-4 flex items-center justify-between">
            <span className="text-lg font-medium">Total</span>
            <span className="text-2xl font-bold">${calculateTotal().toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-[#014DB7]"
          >
            {saving ? 'Creating...' : 'Create Invoice'}
          </Button>
        </div>
      </div>
    </div>
  );
}


export { EditClientModal, AddProductModal, AddServiceModal, LocationModal, AddCardModal, CreateInvoiceModal };
